// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

// redux
import { useSelector } from 'react-redux';

// mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { default as Grid } from '@mui/material/Grid2';

// notistack
import { useSnackbar } from 'notistack';

// project constants
import { BOOKING_COMMUNICATION_SUBTYPE_KEY, COMMUNICATION_TEMPLATE_TYPE } from '@a1/shared';
import { bookingStatuses } from 'constants/bookings';
import userTypes from 'constants/userTypes';

// project utils
import { useGetEntityName } from 'utils/entities';
import handleError from 'utils-ts/handle-error';
import callAzureFunction from 'utils-ts/call-azure-function';
import { getBookingStatus } from 'utils/booking';
import useAppDataSource from 'hooks/useAppDataSource';

// project components
import AddMultipleEmails from 'ui-component/AddMultipleEmails';
import EmailCommunicationMessageInput from 'ui-component/EmailCommunicationMessageInput';
import SmsCommunicationMessageInput from 'ui-component/SmsCommunicationMessageInput';

// ==============================|| ViewBookingDialog ||==============================//

const EmailBookingDialog = ({ open, setOpen, setViewDialogOpen, bookingId = false }) => {
  const appConfig = useSelector((state) => state.appConfig?.data);
  const user = useSelector((state) => state.user.data);

  const { appDataSourceId } = useAppDataSource();

  const [emailRecipients, setEmailRecipients] = useState([]);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [booking, setBooking] = useState({});
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [emailMessageSubject, setEmailMessageSubject] = useState('');
  const [emailMessageBody, setEmailMessageBody] = useState('');
  const [sendSmsReminder, setSendSmsReminder] = useState(false);
  const [smsMessageBody, setSmsMessageBody] = useState('');

  const bookingEntityName = useGetEntityName('booking');
  const respondentEntityName = useGetEntityName('respondent');
  const adminEntityName = useGetEntityName('admin');

  const { Respondent: respondent, affiliateSelected, status } = booking;

  const bookingStatus = useMemo(() => getBookingStatus(status, affiliateSelected), [status, affiliateSelected]);

  const resetSmsReminder = () => {
    setSendSmsReminder(false);
    setSmsMessageBody('');
  };

  const resetEmailReminder = () => {
    setEmailMessageSubject('');
    setEmailMessageBody('');
  };

  const handleClose = () => {
    setEmailRecipients([]);
    resetEmailReminder();
    resetSmsReminder();
    setOpen(false);
    setViewDialogOpen(true);
    closeSnackbar();
  };

  const emailAlreadyExists = (email) => emailRecipients.includes(email);

  const findKeyForEmailAddress = (newEmail) => emailRecipients.findIndex((email) => email === newEmail);

  const removeEmailRecipient = (key) => {
    setEmailRecipients((emailRecipients) => emailRecipients.filter((_, i) => i !== key));
  };

  const removeBookingRespondentEmail = (email) => {
    const key = findKeyForEmailAddress(email);
    if (key >= 0) {
      removeEmailRecipient(key);
    }
  };

  const handleSendRequest = async () => {
    const snackbarProps = {
      variant: 'info',
      message: ''
    };

    const respondentEmailAddress = respondent.emailAddress;

    try {
      setSendingReminder(true);

      const sendEmailReminder = emailAlreadyExists(respondentEmailAddress);

      // send confirmation via email and SMS (if checked)
      if (sendEmailReminder || sendSmsReminder) {
        await callAzureFunction({
          url: `/bookings/${booking.id}/send-confirmation`,
          method: 'post',
          data: {
            bookingId,
            recipients: emailRecipients,
            contactMethods: { emailAddress: sendEmailReminder, contactMobilePhone: sendSmsReminder },
            emailMessageSubject: sendEmailReminder ? emailMessageSubject : undefined,
            emailMessageBody: sendEmailReminder ? emailMessageBody : undefined,
            smsMessageBody: sendSmsReminder ? smsMessageBody : undefined,
            forceSend: true
          },
          appDataSourceId
        });
      }

      // Remove the recipient email from the email recipient array. They don't need to get the confirmation email
      const confirmationEmailRecipients = emailRecipients.filter((recipient) => recipient !== respondentEmailAddress);

      if (confirmationEmailRecipients.length > 0) {
        const promises = confirmationEmailRecipients.map(async (recipient) => {
          await callAzureFunction({
            url: `bookings/${bookingId}/send-confirmation-to-target-email`,
            method: 'post',
            data: {
              entityNames: { respondent: respondentEntityName, booking: bookingEntityName },
              emailAddress: recipient,
              showAppointmentEndTime: false
            },
            appDataSourceId
          });
        });

        await Promise.all(promises);
      }
      snackbarProps.variant = 'success';
      snackbarProps.message = `Reminder ${emailRecipients.length <= 1 ? 'notification has' : 'notifications have'} been sent.`;
      setViewDialogOpen(false);
    } catch {
      snackbarProps.variant = 'error';
      snackbarProps.message = 'Failed to send reminder notification.';
    } finally {
      enqueueSnackbar({
        variant: snackbarProps.variant,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right'
        },
        message: snackbarProps.message
      });
      setSendingReminder(false);
      setEmailRecipients([]);
      resetEmailReminder();
      resetSmsReminder();
    }
  };

  const handleSmsReminderChange = (event) => {
    const { checked } = event.target;
    if (!checked) {
      resetSmsReminder();
      return;
    }
    setSendSmsReminder(true);
  };

  const handleEmailReminderChange = () => {
    if (emailAlreadyExists(respondent.emailAddress)) {
      removeBookingRespondentEmail(respondent.emailAddress);
      resetEmailReminder();
      return;
    }
    setEmailRecipients([...emailRecipients, respondent.emailAddress]);
  };

  const getBooking = useCallback(async () => {
    try {
      setLoadingBooking(true);
      const response = await callAzureFunction({ url: `/bookings/${bookingId}`, method: 'get', appDataSourceId });
      setBooking(response.data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingBooking(false);
    }
  }, [bookingId, appDataSourceId]);

  useEffect(() => {
    if (open && bookingId) {
      getBooking();
    }
  }, [open, bookingId, getBooking]);

  return (
    <>
      <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
        {loadingBooking ? (
          <DialogContent>
            <Skeleton variant="rectangular" height={250} />
          </DialogContent>
        ) : (
          <>
            {booking.id ? (
              <>
                <DialogTitle variant="h3">
                  {bookingEntityName} for {respondent ? `${respondent.firstName} ${respondent.lastName}` : '-'}
                </DialogTitle>
                <DialogContent>
                  <Grid container>
                    {respondent && (
                      <>
                        <Grid size={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="emailReminder"
                                checked={emailAlreadyExists(respondent.emailAddress)}
                                onChange={handleEmailReminderChange}
                                disabled={!respondent.emailAddress}
                              />
                            }
                            label={`Send reminder notification via email to ${respondent.firstName} ${respondent.lastName}`}
                          />
                        </Grid>
                        <Grid size={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="smsReminder"
                                checked={sendSmsReminder}
                                onChange={handleSmsReminderChange}
                                disabled={!respondent.contactMobilePhone || !appConfig?.generalSettings.sendBySmsEnabled}
                              />
                            }
                            label={`Send reminder notification via SMS to ${respondent.firstName} ${respondent.lastName}`}
                          />
                        </Grid>
                        {emailAlreadyExists(respondent.emailAddress) && (
                          <Grid size={12} mb={2}>
                            <EmailCommunicationMessageInput
                              communicationType={COMMUNICATION_TEMPLATE_TYPE.BOOKING}
                              communicationSubtype={BOOKING_COMMUNICATION_SUBTYPE_KEY.CONFIRMATION}
                              communicationEntityName={bookingEntityName}
                              emailMessageSubject={emailMessageSubject}
                              setEmailMessageSubject={setEmailMessageSubject}
                              emailMessageBody={emailMessageBody}
                              setEmailMessageBody={setEmailMessageBody}
                            />
                          </Grid>
                        )}
                        {sendSmsReminder && (
                          <Grid size={12} mb={2}>
                            <SmsCommunicationMessageInput
                              communicationType={COMMUNICATION_TEMPLATE_TYPE.BOOKING}
                              communicationSubtype={BOOKING_COMMUNICATION_SUBTYPE_KEY.CONFIRMATION}
                              communicationEntityName={bookingEntityName}
                              smsMessageBody={smsMessageBody}
                              setSmsMessageBody={setSmsMessageBody}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                    <Grid size={12}>
                      <AddMultipleEmails recipients={emailRecipients} setRecipients={setEmailRecipients} />
                    </Grid>
                    {respondent?.emailAddress && (
                      <Grid size={12} mt={2}>
                        <Typography>
                          Note that <strong>{respondent.emailAddress}</strong> will receive a reminder notification if email option is
                          checked. Whereas all other email recipients will receive the {adminEntityName} notification.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  {/* send booking reminder */}
                  {[userTypes.Admin, userTypes.Manager].includes(user.type) &&
                    (affiliateSelected || bookingStatus?.value === bookingStatuses.Upcoming.value) && (
                      <Button
                        loading={sendingReminder}
                        onClick={handleSendRequest}
                        disabled={
                          (emailRecipients.length <= 0 && !sendSmsReminder) ||
                          (emailAlreadyExists(respondent?.emailAddress) && (!emailMessageSubject.trim() || !emailMessageBody.trim())) ||
                          (sendSmsReminder && !smsMessageBody.trim())
                        }
                      >
                        Send
                      </Button>
                    )}
                  {/* close dialog */}
                  <Button variant="contained" onClick={handleClose} disabled={sendingReminder}>
                    Close
                  </Button>
                </DialogActions>
              </>
            ) : (
              <Typography>{bookingEntityName} not found</Typography>
            )}
          </>
        )}
      </Dialog>
    </>
  );
};

EmailBookingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  setViewDialogOpen: PropTypes.func.isRequired,
  bookingId: PropTypes.string.isRequired
};

export default EmailBookingDialog;
